<template>
  <v-container id="snapshot_list" fluid class="mb-4 pt-0 pa-0">
    <v-row id="snapshot_list_header">
      <v-col align-self="center" cols="auto">
        <v-icon
          class="mr-2"
          color="cyan-dark"
          :icon="labels.SNAPSHOTS_LIST.HEADER_ICON"
          size="large"
        >
        </v-icon>
      </v-col>
      <v-col align-self="center">
        <h2 class="itemis-analyze-title">{{ labels.SNAPSHOTS_LIST.HEADER_TEXT }}</h2>
      </v-col>
    </v-row>

    <v-row>
      <v-col cols="3" sm="6" md="4">
        <analyze-search v-model="search" :hide-details="true" />
      </v-col>
      <v-col>
        <input id="fileUpload" ref="upload" type="file" class="d-none" @change="handleFileImport" />
        <v-btn
          data-test="open-btn"
          color="cyan-dark"
          :class="['ml-5', 'btn-open-snapshot', 'px-10']"
          variant="flat"
          :loading="snapshotStore.status.uploading"
          :prepend-icon="CONSTANTS.ICONS_MDI.ICON_ARROW_EXPAND_RIGHT"
          @click.stop="
            () => {
              !!currentSelectedProject && upload !== null ? upload.click() : checkSelectProject()
            }
          "
        >
          {{ labels.BUTTONS.IMPORT_SNAPSHOT }}
        </v-btn>
      </v-col>
    </v-row>
  </v-container>

  <div v-if="projectStore.currentProject" :class="['pa-0', 'mt-16']">
    <analyze-data-table
      table-id="snapshots-table-id"
      :headers="snapshotHeaders"
      :fixed-header="true"
      :fixed-footer="true"
      :max-height="40"
      :items="snapshotItems"
      :total-items="snapshotStore.pagination.totalItems"
      :items-per-page="snapshotStore.pagination.itemsPerPage"
      :loading="snapshotStore.status.loading"
      :search="search"
      data-type="snapshots"
      :has-server-sided-pagination="true"
      :has-delete-action="true"
      :load-items="loadItems"
      @select="selectSnapshots"
      @delete="setSnapshotToDelete"
    />

    <ConfirmDeletionDialog
      v-if="selectedSnapshotToDelete"
      :key="`dialog-${selectedSnapshotToDelete?.id}`"
      :element-to-delete="selectedSnapshotToDelete"
      :get-element-name="(p?: Snapshot) => p?.name ?? 'My Snapshot'"
      :element-type-name="labels.DELETE_ELEMENT.SNAPSHOT"
      :is-deleting-element="isDeletingSnapshot"
      :extra-confirmation-required="true"
      @confirm-deletion="removeSnapshot"
      @cancel-deletion="selectedSnapshotToDelete = undefined"
    />
  </div>
</template>

<script lang="ts" setup>
  import CONSTANTS from '@/constants/Config.constants'
  import { Project, Snapshot } from '@/models'
  import * as HELPERS from '@/utils/helpers'
  import notifMessage from '@/translations/notifMessage.json'
  import labels from '@/translations/labels.json'
  import AnalyzeSearch from '@/components/Inputs/AnalyzeSearch.vue'
  import ConfirmDeletionDialog from '@/components/ConfirmDeletionDialog/ConfirmDeletionDialog.vue'
  import { computed, ref } from 'vue'
  import { useSnapshotStore } from '@/stores/snapshotStore'
  import { useProjectStore } from '@/stores/projectStore'
  import AnalyzeDataTable from '@/components/AnalyzeDataTable/AnalyzeDataTable.vue'
  import { AnalyzeTableHeader, AnalyzeTableItem } from '@/models/table.model'

  const projectStore = useProjectStore()
  const snapshotStore = useSnapshotStore()

  const search = ref<string>('')

  const selectedSnapshotToDelete = ref<Snapshot | undefined>()
  const isDeletingSnapshot = ref<boolean>(false)

  const upload = ref<HTMLInputElement | null>(null)
  const isSelecting = ref(false)
  const selectedFile = ref<File | undefined>(undefined)
  const currentSelectedProject = computed((): Project | undefined => {
    return projectStore.currentProject
  })

  const emit = defineEmits<{
    (event: 'selectProject', value: Project): void
    (event: 'checkSelectProject', value: Project | undefined): void
  }>()

  const snapshotHeaders = ref<AnalyzeTableHeader[]>([
    { title: '', key: 'selected', width: '58' },
    { title: 'Name', key: 'name' },
    { title: 'Updated', key: 'updated' },
    { title: '', key: 'actions', align: 'end', sortable: false },
  ])

  const snapshotItems = computed((): AnalyzeTableItem<Snapshot>[] =>
    snapshotStore.sortedSnapshots.map((snapshot) => ({
      name: snapshot.name,
      updated: new Date(HELPERS.getCreationDateOnly(snapshot.date as string) || ''),
      value: snapshot,
      selected: snapshot.id === snapshotStore.currentSnapshot?.id,
    })),
  )

  const loadItems = async ({ page, itemsPerPage }: { page: number; itemsPerPage: number }) => {
    await snapshotStore.loadSnapshotsFromApi({
      projectId: projectStore.currentProject?.id || 0,
      page,
      itemsPerPage,
    })
  }

  function selectSnapshots(snapshot?: Snapshot): void {
    if (snapshot === undefined) {
      return
    }

    const currentSnapshot = snapshot

    snapshotStore.setCurrentSnapshot(snapshot)

    const message = HELPERS.replaceVariableInString(
      labels.ALERT_VIEW.SNAPSHOT_SELECTED_ALERT,
      currentSnapshot.name,
    )
    HELPERS.showTopAlertMessage(true, 'info', message)
  }

  function setSnapshotToDelete(snapshot?: Snapshot): void {
    if (snapshot) {
      selectedSnapshotToDelete.value = snapshot
    }
  }

  async function removeSnapshot(snapshot: Snapshot): Promise<void> {
    if (!projectStore.currentProject) {
      HELPERS.showTopAlertMessage(true, 'error', notifMessage.ERROR_ALERTS.DELETE_SNAPSHOT_ERROR)
      return
    }

    isDeletingSnapshot.value = true

    try {
      await snapshotStore.deleteSnapshot({
        projectId: projectStore.currentProject.id,
        snapshot,
      })

      HELPERS.showTopAlertMessage(true, 'success', notifMessage.SUCCESS_ALERTS.SNAPSHOT_DELETED)
      snapshotStore.setCurrentSnapshot(undefined)
    } catch {
      HELPERS.showTopAlertMessage(true, 'error', notifMessage.ERROR_ALERTS.DELETE_SNAPSHOT_ERROR)
    } finally {
      isDeletingSnapshot.value = false
      selectedSnapshotToDelete.value = undefined
    }
  }

  function handleFileImport(event: Event): void {
    isSelecting.value = true
    HELPERS.showTopAlertMessage(true, 'info', notifMessage.SUCCESS_ALERTS.SNAPSHOT_UPLOADING)
    onFileChanged(event)
  }

  async function onFileChanged(e: Event) {
    selectedFile.value = (e.target as HTMLInputElement).files?.[0]

    if (selectedFile.value !== undefined && HELPERS.hasValidExtension(selectedFile.value.name)) {
      await snapshotStore.uploadSnapshot(
        selectedFile.value,
        currentSelectedProject.value as Project,
      )
    } else {
      HELPERS.showTopAlertMessage(
        true,
        'error',
        notifMessage.ERROR_ALERTS.WRONG_FORMAT.FILE_NOT_SUPPORTED,
      )
    }
  }

  function checkSelectProject(): void {
    emit('checkSelectProject', currentSelectedProject.value)
  }
</script>

<style lang="scss" scoped>
  .header {
    padding: 16px;

    h3 {
      margin-bottom: 0;
      font-weight: 500;
    }

    .options {
      float: right;

      mat-form-field {
        margin-left: 12px;
      }
    }
  }
  .snap_card {
    margin-bottom: 10px;
  }
  .timeAgo {
    text-align: center;

    margin: auto;
  }

  .content {
    clear: both;

    mat-nav-list {
      padding-top: 0;
    }

    a {
      border-top: solid 1px rgb(var(--v-theme-grey-medium));

      h3 {
        font-weight: 500;
      }

      &:first-child {
        border-top: none;
      }
    }
  }

  .d-flex.col-sm-3.col-6 {
    max-width: 341px;
    min-width: 341px;
  }

  .snapshot-List-header {
    font-size: 25px;
    font-weight: 300;
    color: rgb(var(--v-theme-font-color));
  }

  .snapshot-List-subheader {
    font-size: 15px;
    font-weight: 200;
    color: rgb(var(--v-theme-font-color));
  }

  .snapshot-List-searchfield-header {
    font-size: 15px;
    color: rgb(var(--v-theme-font-color));
  }

  .snapshot-name {
    font-weight: bolder;
  }

  .snapshot-list {
    padding: 10px;
    overflow: hidden;
    max-height: 780px;
  }

  .snapshot-list-card {
    padding-top: 30px;
  }
</style>
